import { useState } from 'react';

import { Check, ChevronDown } from 'lucide-react';

import { Button } from 'components/Button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/Popover';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

import { VehicleTypeOption } from './laneRecordFormatting';

type VehicleTypeFilterProps = {
  options: VehicleTypeOption[];
  // Null means every type.
  value: Maybe<string>;
  onChange: (value: Maybe<string>) => void;
};

// Same look as the lane tier picker on the TMS lane history chart.
export function VehicleTypeFilter({
  options,
  value,
  onChange,
}: VehicleTypeFilterProps) {
  const [open, setOpen] = useState(false);
  const allOptions: { value: Maybe<string>; label: string }[] = [
    { value: null, label: 'All types' },
    ...options,
  ];
  const selectedLabel =
    allOptions.find((option) => option.value === value)?.label ?? 'All types';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='underline'
          type='button'
          className='h-6 px-0 text-[10px] text-brand-500'
          buttonNamePosthog={
            ButtonNamePosthog.QuickQuoteSuperDispatchVehicleTypeFilter
          }
          data-testid='super-dispatch-vehicle-type-filter'
        >
          {`Vehicle type: ${selectedLabel}`}
          <ChevronDown className='h-3 w-3' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='mx-1 w-48 p-3'>
        <ul className='text-sm'>
          {allOptions.map((option) => (
            <li
              key={option.label}
              className={cn(
                'flex flex-row justify-between text-[10px] py-0.5 hover:bg-neutral-100 cursor-pointer rounded-[4px]'
              )}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
              {option.value === value && (
                <Check className='h-3 w-3 text-success-500' />
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
